<template>
  <div>
    <VerificationInfo
      class="mb-5 cursor-pointer"
      @click.native="openVerificationModal()"
      v-if="
        !this.userVerification.is_email_validation_required ==
          this.userVerification.is_email_validated ||
        !this.userVerification.is_cellphone_validation_required ==
          this.userVerification.is_cellphone_validated ||
        !(
          this.userVerification.is_nin_validation_required == false ||
          (this.userVerification.is_nin_validation_required &&
            this.userVerification.nin_validated)
        )
      "
    />
    <!-- Filters -->
    <Filters :elements="filterElements" @selectedFilters="selectedFilters" />

    <!-- VM List Table -->
    <List
      :header="header"
      :data="data"
      :pagination="pagination"
      :loading="loading"
      @updatePage="updatePage"
      @click="clickField"
      detailUrl="/compute/virtual-machines"
      :moduleName="`servers.vm`"
      :moduleCreateEvent="`subHeaderDetailOpen`"
      :moduleSearch="search"
    >
      <template v-slot:item.os="{ item }">
        <i v-if="item.os" :class="`icon fab fa-${item.os.toLowerCase()}`"></i>
        <i v-else :class="`icon fas fa-server`"></i>
      </template>

      <template v-slot:item.name="{ item }">
        <div class="name">
          {{ item.name }}
          <p>
            {{ `${item.actual_cpu} Thread ${item.actual_ram / 1024}GB Ram` }}
          </p>
        </div>
      </template>

      <template v-slot:item.status="{ item }">
        <Label :class="item.status.toLowerCase()">{{
          $capitalize(item.status)
        }}</Label>
      </template>

      <template v-slot:item.is_locked="{ item }">
        <LockButton
          icon="fas fa-lock"
          class="danger"
          v-if="item.is_locked"
        ></LockButton>
        <LockButton icon="fas fa-unlock" class="primary" v-else></LockButton>
      </template>

      <template v-slot:item.action="{ item }">
        <ActionButton
          icon="fas fa-eye"
          title="Open Detail"
          @click.native="clickField(item)"
        ></ActionButton>
        <ActionButton
          icon="fas fa-terminal"
          title="Open Console"
          target="_blank"
          :href="`https://console.plusclouds.com/?vmID=${item.id}`"
        ></ActionButton>
        <ActionButton icon="fas fa-sync-alt" title="Restart"></ActionButton>

        <DeleteButton
          icon="fad fa-trash"
          :title="$t('global.delete')"
          @click.native="openDeleteHandler(item)"
        ></DeleteButton>
      </template>
    </List>

    <!-- Create VM Popup -->
    <DetailModal
      :title="$t('servers.newVM')"
      :show="showDetail"
      @confirm="confirmModal()"
      @closeDetail="closeDetail()"
      :resetForm="resetForm"
    >
      <b-form-group :label="$t('forms.vm.dcNode')">
        <multiselect
          v-model="form.dcNode"
          :options="dcNodeList"
          track-by="id"
          label="name"
          :loading="loadingDcNode"
          @select="selectedDcNode"
          :allowEmpty="false"
          :show-labels="false"
          :searchable="true"
          :placeholder="$t('forms.vm.dcNode')"
          :show-no-results="false"
        />
      </b-form-group>

      <TabMenu v-if="showDetail && !loadingControlCreateBasic">
        <TabItem name="forms.vm.basic" :selected="showCreateBasic">
          <VMCreateBasic
            :confirmModal="confirmCreate"
            :dcNode="form.dcNode"
            @startCreatedControl="startCreatedControl"
            @resetDetailForm="resetDetailForm"
            v-if="showCreateBasic"
          />

          <InfoBox v-else>
            {{ $t('forms.vm.basicFormDisableInfo') }}
          </InfoBox>
        </TabItem>
        <TabItem name="forms.vm.expert" :selected="!showCreateBasic">
          <VMCreateAdvanced
            :confirmModal="confirmCreate"
            :dcNode="form.dcNode"
            @resetDetailForm="resetDetailForm"
          />
        </TabItem>
      </TabMenu>
    </DetailModal>

    <VerificationModal />
    <DeleteOperation :url="deleteOperationUrl" @updateList="getVMList" />
  </div>
</template>

<script>
// Utils
import { SET_BREADCRUMB } from '@/core/services/store/breadcrumbs.module'
import { SET_CUSTOMHEADERPARAMS } from '@/core/services/store/customsubheader.module'
import EventBus from '@/event-bus'

// Services
import ApiService from '@/core/services/api.service'
import { mapGetters } from 'vuex'

var isCreatedInterval = null

export default {
  name: 'VirtualMachines',
  data() {
    return {
      search: '',
      filters: '',
      showDetail: false,
      loading: false,
      confirmCreate: false,
      resetForm: false,
      header: [
        { text: '', value: 'os', size: '100px', clicked: true },
        {
          text: 'global.name',
          value: 'name',
          size: '2fr',
          clicked: true,
        },
        { text: 'global.status', value: 'status' },
        {
          text: 'global.ipAddress',
          value: 'ip_addr',
          copyField: true,
        },
        { text: 'global.connectedTo', value: 'connected_to' },
        { text: 'global.lock', value: 'is_locked' },
        { text: 'global.actions', value: 'action' },
      ],
      data: [],
      pagination: {},
      meta: {},
      filterElements: [
        {
          type: 'button',
          name: 'os',
          value: 'all',
          items: [
            { name: 'filters.all', value: 'all' },
            { name: 'filters.windows', value: 'Microsoft Windows' },
            { name: 'filters.linux', value: 'Linux' },
          ],
        },
        {
          type: 'dropdown',
          name: 'status',
          value: 'all',
          items: [
            { name: 'filters.allTypes', value: 'all' },
            { name: 'filters.running', value: 'running' },
            { name: 'filters.halted', value: 'halted' },
            { name: 'filters.paused', value: 'paused' },
            { name: 'filters.preparing', value: 'preparing' },
          ],
        },
        {
          type: 'dropdown',
          name: 'position',
          value: 'id|desc',
          items: [
            { name: 'filters.idDesc', value: 'id|desc' },
            { name: 'filters.idAsc', value: 'id|asc' },
            { name: 'filters.nameDesc', value: 'name|desc' },
            { name: 'filters.nameAsc', value: 'name|asc' },
          ],
        },
      ],
      form: {
        dcNode: '',
      },
      dcNodeList: [],
      loadingDcNode: false,
      loadingControlCreateBasic: true,
      showCreateBasic: false,
      deleteOperationUrl: '',
    }
  },
  computed: {
    ...mapGetters(['userVerification']),
  },
  created() {
    this.getDatacenterNodes()
    this.getVMList()
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: 'servers.vm',
        route: '/compute/virtual-machines',
      },
    ])
    this.$store.dispatch(SET_CUSTOMHEADERPARAMS, {
      menuList: [
        {
          title: 'servers.compute',
        },
        {
          title: 'servers.vm',
          route: '/compute/virtual-machines',
        },
      ],
      searchPlaceholder: 'servers.searchVM',
      detailPlaceholder: 'servers.newVM',
    })
    EventBus.$on(`subHeaderSearch`, this.listenersubHeaderSearch)
    EventBus.$on(`subHeaderDetailOpen`, this.listenersubHeaderDetailOpen)
  },
  beforeDestroy() {
    EventBus.$off(`subHeaderSearch`, this.listenersubHeaderSearch)
    EventBus.$off(`subHeaderDetailOpen`, this.listenersubHeaderDetailOpen)
  },
  methods: {
    listenersubHeaderSearch(payload) {
      this.search = payload
      this.getVMList()
    },
    listenersubHeaderDetailOpen(payload) {
      if (
        !this.userVerification.is_email_validation_required ==
          this.userVerification.is_email_validated ||
        !this.userVerification.is_cellphone_validation_required ==
          this.userVerification.is_cellphone_validated ||
        !(
          this.userVerification.is_nin_validation_required == false ||
          (this.userVerification.is_nin_validation_required &&
            this.userVerification.nin_validated)
        )
      ) {
        this.openVerificationModal()
      } else {
        this.showDetail = payload
      }
    },
    getVMList() {
      this.loading = true

      const { current_page } = this.meta

      let $query = `iaas/virtual-machines/overview?page=${
        current_page ? current_page : 1
      }`

      if (this.search) $query += `&name=${this.search}`

      if (this.filters) $query += `&${this.filters}`

      if ($query.includes('position=') == false) $query += `&position=id|desc`

      ApiService.get($query)
        .then(({ data }) => {
          this.data = data.data
          this.pagination = data.meta ? data.meta.pagination : {}

          this.loading = false
        })
        .catch((err) => {
          console.log(err)
          this.data = []
          this.loading = false
        })
    },
    updatePage(value) {
      this.meta = value

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    },
    clickField(item) {
      this.$router.push('/compute/virtual-machines/' + item.id)
    },
    confirmModal() {
      this.confirmCreate = true
      setTimeout(() => {
        this.confirmCreate = false
      }, 200)
    },
    resetDetailForm(closeDetail = false) {
      if (closeDetail) {
        this.closeDetail()
        this.getVMList()
      }

      this.resetForm = true
      setTimeout(() => {
        this.resetForm = false
      }, 200)
    },
    closeDetail() {
      this.showDetail = false

      this.resetForm = true
      setTimeout(() => {
        this.resetForm = false
      }, 200)
    },
    selectedFilters(value) {
      this.filters = value
      this.getVMList()
    },
    startCreatedControl(closeDetail = false, res, serverName) {
      if (closeDetail) {
        this.closeDetail()
      }
      this.resetForm = true
      setTimeout(() => {
        this.resetForm = false
      }, 200)

      if (isCreatedInterval) {
        clearInterval(isCreatedInterval)

        isCreatedInterval = setInterval(() => {
          ApiService.get(
            'iaas/virtual-machines/overview?position=id|desc'
          ).then(({ data }) => {
            data.data.map((item) => {
              if (item.name.includes(serverName)) {
                clearInterval(isCreatedInterval)
                this.getVMList()
              }
            })
          })
        }, 5000)
      } else {
        isCreatedInterval = setInterval(() => {
          ApiService.get(
            'iaas/virtual-machines/overview?position=id|desc'
          ).then(({ data }) => {
            data.data.map((item) => {
              if (item.name.includes(serverName)) {
                clearInterval(isCreatedInterval)
                this.getVMList()
              }
            })
          })
        }, 5000)
      }
    },
    // datacenter node begin
    getDatacenterNodes() {
      this.getDatacenterNodeList()
        .then((res) => {
          this.setDatacenterNodeList(res)
        })
        .catch(() => {
          this.dcNodeList = []
        })
    },
    getDatacenterNodeList() {
      this.loadingDcNode = true
      return new Promise((resolve, reject) => {
        ApiService.get(`iaas/datacenter-nodes?orderBy=position|ASC`)
          .then(({ data }) => {
            resolve(data)
            this.loadingDcNode = false
          })
          .catch((err) => {
            reject(err)
            this.loadingDcNode = false
          })
      })
    },
    setDatacenterNodeList(res) {
      if (res && res.data && res.data.length > 0) {
        this.dcNodeList = res.data.filter(
          (f) => f.name.toLowerCase().includes('kapatılacak') == false
        )
      }
    },
    selectedDcNode(selectedDc) {
      this.loadingControlCreateBasic = true
      ApiService.get(
        `iaas/virtual-machines/wizards/create?datacenterNode=${selectedDc.id}`
      )
        .then(({ data }) => {
          console.log(data)
          this.showCreateBasic = true
        })
        .catch((err) => {
          console.log(err)
          this.showCreateBasic = false
        })
        .finally(() => {
          this.loadingControlCreateBasic = false
        })
    },
    // datacenter node end
    openVerificationModal() {
      this.$bvModal.show('user-verification-modal')
    },
    openDeleteHandler(item) {
      if (item.status != 'halted') {
        EventBus.$emit('notificationError', {
          title: '',
          description: this.$t('servers.vmMustBeHalted'),
        })
      } else {
        this.deleteOperationUrl = `iaas/virtual-machines/${item.id}`
        this.$bvModal.show('modal-deleteOperation')
      }
    },
  },
  watch: {
    meta: {
      handler() {
        this.getVMList()
      },
      deep: true,
    },
  },
  components: {
    VMCreateBasic: () => import('@/components/forms/VMCreateBasic'),
    VMCreateAdvanced: () => import('@/components/forms/VMCreateAdvanced'),
    VerificationModal: () =>
      import('@/components/userVerifications/VerificationModal'),
    VerificationInfo: () => import('@/components/userVerifications/Info'),
  },
}
</script>
